<template>
  <div class="px-4 py-2 border border-gray-300 rounded">
    <div class="flex justify-between">
      <div class="flex items-center space-x-4">
        <div>
          <input
            type="checkbox"
            v-model="shouldProcess"
            :id="`should-process-${_uid}`"
            :disabled="!canBeSelected"
            v-on:change="toggleProcessing()"
          />
        </div>
        <label :for="`should-process-${_uid}`">
          <div class="text-lg">
            {{ productName }}
          </div>
          <div class="text-gray-500">
            {{ variant.variant_name }} ({{ variant.id }})
          </div>
        </label>
      </div>
      <div class="flex flex-col items-end space-y-1 whitespace-no-wrap">
        <span
          v-if="hasShipped"
          class="px-2 bg-green-500 text-xs text-white rounded-full"
        >
          Shipped {{ this.dateFormat(product.DateShipped) }}
        </span>
        <span v-else class="px-2 text-xs"> Not Shipped </span>
        <div>
          <span v-if="isDropShip">Drop Ship</span>
          <span
            v-else
            class="px-2 text-xs text-white rounded-full"
            :class="{ 'bg-red-500': !stock, 'bg-green-500': stock }"
          >
            {{ stock }} in stock
          </span>
        </div>
      </div>
    </div>
    <div v-if="canClaim">
      <div
        v-if="showQuantityAvailable"
        class="w-full flex justify-end items-center space-x-4"
      >
        Claim(s) Available: {{ orderedQuantity }}
      </div>
      <div
        v-if="canBeReshipped"
        class="w-full flex justify-end items-center space-x-4"
      >
        <span>Quantity:</span>
        <input
          v-model="originalVariantQuantity"
          v-on:input="updateProcessing()"
          class="w-24 px-1 border rounded"
          :class="{
            'border-green-500': shouldProcess && isOriginalVariantQuantityValid,
            'border-red-500': shouldProcess && !isOriginalVariantQuantityValid
          }"
        />
      </div>
      <div v-else class="w-full flex justify-end items-center space-x-4">
        <span v-if="isNotShippable" class="text-right text-gray-500"
          >This product can no longer be reshipped.</span
        >
        <span v-else>Not in stock!</span>
        <span
          v-if="canBeReplaced"
          v-on:click="toggleReplacements"
          class="underline cursor-pointer"
        >
          <span v-text="showReplacements ? 'hide' : 'show'"></span>
          possible replacements
        </span>
      </div>
      <div
        v-if="reshipOnly"
        class="w-full flex justify-end items-center space-x-4 text-red-600"
      >
        {{ reshipOnlyText }}
      </div>
      <div v-if="showReplacements">
        <div v-if="loadingReplacements">loading ...</div>
        <div v-else>
          <form>
            <table class="w-full border-collapse border border-gray-300">
              <tr>
                <th class="border border-gray-300"></th>
                <th class="border border-gray-300">Variant</th>
                <th class="border border-gray-300">Qty</th>
                <th class="border border-gray-300">Reship Qty</th>
                <th class="border border-gray-300">Status</th>
              </tr>

              <tr
                v-for="(variant, index) in filteredReplacements"
                :key="index"
                class="bg-white even:bg-gray-100"
              >
                <td class="px-2 text-center border border-gray-300">
                  <input
                    type="radio"
                    v-model="replacement"
                    :value="variant.id"
                    :id="`replacement_${_uid}_${index}`"
                    name="replacement"
                  />
                </td>
                <td class="px-2 border border-gray-300">
                  <label :for="`replacement_${_uid}_${index}`">
                    {{ variant.variant_name }}
                  </label>
                </td>
                <td class="px-2 text-right border border-gray-300">
                  {{ variant.quantity }}
                </td>
                <td class="text-center border border-gray-300">
                  <input
                    v-model="variant.quantityToProcess"
                    v-on:input="updateProcessing()"
                    class="w-24 px-1 border rounded"
                    :class="{
                      'border-green-500':
                        shouldProcess &&
                        replacement == variant.id &&
                        isInventoryQuantityValid(variant),
                      'border-red-500':
                        shouldProcess &&
                        replacement == variant.id &&
                        !isInventoryQuantityValid(variant)
                    }"
                  />
                </td>
                <td class="text-center border border-gray-300">
                  Available Now!
                </td>
              </tr>

              <tr class="bg-white even:bg-gray-100">
                <td class="px-2 text-center border border-gray-300">
                  <input
                    type="radio"
                    v-model="replacement"
                    :value="product.variant_id"
                    :id="`replacement_${_uid}_last`"
                    name="replacement"
                  />
                </td>
                <td class="px-2 border border-gray-300">
                  <label :for="`replacement_${_uid}_last`">{{
                    variant.variant_name
                  }}</label>
                </td>
                <td class="px-2 text-right border border-gray-300">
                  {{ stock }}
                </td>
                <td class="text-center border border-gray-300">
                  <input
                    v-model="originalReplacementQuantity"
                    v-on:input="updateProcessing()"
                    class="w-24 px-1 border rounded"
                    :class="{
                      'border-green-500':
                        shouldProcess &&
                        replacement == product.variant_id &&
                        isOriginalReplacementQuantityValid,
                      'border-red-500':
                        shouldProcess &&
                        replacement == product.variant_id &&
                        !isOriginalReplacementQuantityValid
                    }"
                  />
                </td>
                <td class="text-center font-bold border border-gray-300">
                  <label :for="`replacement_${_uid}_last`">
                    Original Variant:<br />Select for discount creation!
                  </label>
                </td>
              </tr>
            </table>
          </form>
          <div v-if="replacement">
            <button
              v-on:click="replacement = null"
              class="mt-2 py-1 px-2 bg-gray-100 hover:bg-gray-300 border border-gray-300 cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="hasShipped && !hasFulfillmentUploaded"
      class="text-right text-gray-500"
    >
      Not eligible since tracking hasn’t uploaded to Shopify
    </div>
    <div v-else class="text-right text-gray-500">
      This product does not qualify for exception claim!
    </div>
  </div>
</template>

<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import reshipOnly from "@/mixins/reshipOnly";

const Inventory = ApiFactory.get("inventories");

export default {
  name: "ExceptionClaimProduct",
  props: {
    product: {
      type: Object,
      required: true
    },
    shopifyLineItem: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      replacement: null,
      replacements: [],
      originalReplacementQuantity: null,
      showReplacements: false,
      loadingReplacements: false,
      loadedReplacements: false,
      shouldProcess: false,
      originalVariantQuantity: null
    };
  },
  mixins: [reshipOnly],
  async beforeMount() {},
  computed: {
    showQuantityAvailable() {
      return "refundableQuantity" in this.shopifyLineItem;
    },
    filteredReplacements() {
      return this.replacements
        .map(variant => {
          variant.quantity = variant.inventory_counts.reduce(
            (accumulator, currentObject) => {
              return (
                accumulator +
                (parseInt(currentObject.total_for_sale_qoh) -
                  parseInt(currentObject.committed_quantity))
              );
            },
            0
          );
          return variant;
        })
        .filter(variant => {
          return (
            variant.deleted_at === null &&
            variant.selling_on_website &&
            variant.quantity > 0 &&
            !variant.jumbo
          );
        });
    },
    productName() {
      return this.variant?.product?.name;
    },
    variant() {
      return this.product?.variant;
    },
    canClaim() {
      return this.hasShipped && this.hasFulfillmentUploaded;
    },
    hasFulfillmentUploaded() {
      return (
        Boolean(this.product?.FulfillmentStatus) ||
        Boolean(this.shopifyLineItem?.fulfillmentStatus === "fulfilled")
      );
    },
    hasShipped() {
      return Boolean(this.product?.DateShipped);
    },
    isNotShippable() {
      return !!this.variant?.deleted_at;
    },
    isDropShip() {
      return this.variant?.dropship;
    },
    stock() {
      return this.variant?.inventory_counts.reduce(
        (accumulator, currentObject) => {
          return (
            accumulator +
            (parseInt(currentObject.total_for_sale_qoh) -
              parseInt(currentObject.committed_quantity))
          );
        },
        0
      );
    },
    canBeSelected() {
      if (!this.canClaim) {
        return false;
      }

      if (this.isDropShip) {
        return true;
      }

      if (!this.stock) {
        return false;
      }

      return true;
    },
    canBeReplaced() {
      return !this.stock && !this.isDropShip;
    },
    canBeReshipped() {
      if (this.isNotShippable) {
        return false;
      }
      return this.stock || this.isDropShip;
    },
    orderedQuantity() {
      return Number(this.shopifyLineItem?.refundableQuantity);
    },
    isOriginalVariantQuantityValid() {
      return (
        Number(this.originalVariantQuantity) > 0 &&
        Number(this.originalVariantQuantity) <= this.orderedQuantity
      );
    },
    isOriginalReplacementQuantityValid() {
      return (
        Number(this.originalReplacementQuantity) > 0 &&
        Number(this.originalReplacementQuantity) <= this.orderedQuantity
      );
    },
    quantityToProcess() {
      if (!this.shouldProcess) {
        return 0;
      }

      if (this.isNotShippable) {
        return this.orderedQuantity;
      }

      if (!this.replacement) {
        return Number(this.originalVariantQuantity);
      }

      if (this.replacement == this.product.variant_id) {
        return Number(this.originalReplacementQuantity);
      }

      let element = this.filteredReplacements.find(
        item => item.id == this.replacement
      );
      if (!element) {
        return 0;
      }

      return Number(element.quantityToProcess);
    }
  },
  watch: {
    replacement(value) {
      this.shouldProcess = Boolean(value);
      this.toggleProcessing();
    }
  },
  methods: {
    toggleReplacements() {
      this.showReplacements = !this.showReplacements;

      if (this.showReplacements && !this.loadedReplacements) {
        this.loadReplacements();
      }
    },
    loadReplacements() {
      if (this.product?.variant?.jumbo) {
        this.replacements = [];
        return;
      }
      this.loadingReplacements = true;

      Inventory.replacements(this.variant.product.product_id)
        .then(response => {
          this.loadedReplacements = true;
          this.replacements = response.data.body.map(item => {
            item.quantityToProcess = null;
            return item;
          });
        })
        .finally(() => {
          this.loadingReplacements = false;
        });
    },
    dateFormat(dateString, options) {
      let date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        ...{ year: "numeric", month: "long", day: "numeric" },
        ...options
      });
    },
    toggleProcessing() {
      let event = this.shouldProcess
        ? "add-to-processing"
        : "remove-from-processing";

      this.$emit(event, this.processingValues());
    },
    updateProcessing() {
      if (!this.shouldProcess) {
        return;
      }
      this.$emit("update-processing", this.processingValues());
    },
    processingValues() {
      return {
        variant_id: this.product.variant_id,
        sku: this.product.SKU, // Needed for SR check
        orderedQuantity: this.orderedQuantity,
        replacement: this.replacement,
        itemNumber: this.product.ItemNumber,
        quantity: this.quantityToProcess,
        isNotShippable: this.isNotShippable,
        dropship: this.isDropShip,
        sellingOnWebsite: this.variant.selling_on_website
      };
    },
    diffInDays(minuend, subtrahend) {
      return Math.ceil(
        (minuend.getTime() - subtrahend.getTime()) / (24 * 60 * 60 * 1000)
      );
    },
    isInventoryQuantityValid(inventory) {
      if (Number(inventory.quantityToProcess) <= 0) {
        return false;
      }

      if (Number(inventory.quantityToProcess) > this.orderedQuantity) {
        return false;
      }

      return true;
    }
  }
};
</script>
